import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "page has-compressed-header" }
const _hoisted_2 = {
  key: 2,
  class: "section primary-header"
}
const _hoisted_3 = { class: "level container is-fluid" }
const _hoisted_4 = { class: "level-left" }
const _hoisted_5 = { class: "back-arrow" }
const _hoisted_6 = { class: "title is-4 flow-title" }
const _hoisted_7 = { class: "flow-status is-flex" }
const _hoisted_8 = { class: "level-right" }
const _hoisted_9 = { class: "level-item" }
const _hoisted_10 = { class: "button-content" }
const _hoisted_11 = { class: "level-item" }
const _hoisted_12 = { class: "button-content" }
const _hoisted_13 = { class: "button-content" }
const _hoisted_14 = {
  key: 1,
  class: "level-item"
}
const _hoisted_15 = { class: "button-content" }
const _hoisted_16 = { class: "level-item" }

import { LOAD_FLOW, LOAD_FLOWS, SAVE_FLOW, UPDATE_STATUS } from '@/store/bot-designer/actionTypes';
import { MutationPayload, useStore } from 'vuex';
import { ADD_PATH, UPDATE_PATH, REMOVE_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { ADD_STATE, REMOVE_STATE, UPDATE_STATE } from '@/store/bot-designer/states/mutationTypes';
import Toast from '@/shared/services/toast';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import EditableText from '@/shared/components/EditableText.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import BackArrow from 'vue-material-design-icons/ArrowLeft.vue';
import {
  MAKE_DIRTY,
  RESET_CHANGE_TRACKER,
  SET_FLOW_NAME,
  SET_IS_SAVE_FLOW_IN_PROGRESS,
} from '@/store/bot-designer/mutationTypes';
import {
  SET_TRIGGER,
  UPDATE_SCHEDULE_CONDITION,
  UPDATE_TRIGGER_CONDITIONS,
  UPDATE_AUDIENCE_CONDITIONS,
  UPDATE_TARGET_CONTACT_TYPES,
} from '@/store/bot-designer/triggers/mutationTypes';
import { ChangeType } from '@/features/bots/models/designer';
import { useRouter, onBeforeRouteUpdate, onBeforeRouteLeave } from 'vue-router';
import { flowsApi } from '@/features/bots/services/flows';
import { ParentViewModel } from '@/features/bots/models/parentViewModel';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import appInsights from '@/shared/services/appInsights';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import BotPreviewModal from '@/features/bots/components/botTestTool/BotPreviewModal.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import PauseIcon from 'vue-material-design-icons/Pause.vue';
import PlayIcon from 'vue-material-design-icons/Play.vue';
import SaveIcon from 'vue-material-design-icons/ContentSave.vue';
import AdvancedTooltip from '@/shared/components/AdvancedTooltip.vue';
import UpgradeModal from '@/features/bots/components/UpgradeModal.vue';
import { LOAD_INTENTS } from '@/store/intents/actionTypes';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'Flow',
  props: {
  flowId: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const router = useRouter();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const flowName = computed(() => store.getters.flowName);
const hasMadeChanges = computed(() => store.getters.hasMadeChanges);
const isSaveFlowInProgress = computed(() => store.getters.isSaveFlowInProgress);
const isFlowInvalid = computed(() => store.getters.isFlowInvalid);
const isCurrentFlowTriggerable = computed(() => store.getters.isCurrentFlowTriggerable);
const currentFlowStatus = computed<FlowStatus>(() => store.getters.currentFlowStatus);
const isTransitioningToAnInactiveFlow = computed<boolean>(() => store.getters.isTransitioningToAnInactiveFlow);
const parentFlows = computed<ParentViewModel[]>(() => store.getters.parentFlows);
const parentAnswers = computed<ParentViewModel[]>(() => store.getters.parentAnswers);
const isWorkspaceFeatureEnabled = (feature: string) => computed(() => store.getters.isWorkspaceFeatureEnabled(feature));

const loadFlow = (flowId: string) => store.dispatch(LOAD_FLOW, flowId);
const saveFlow = () => store.dispatch(SAVE_FLOW);
const loadFlows = (forceLoad: boolean = false) => store.dispatch(LOAD_FLOWS, forceLoad);
const loadIntents = (forceLoad: boolean = false) => store.dispatch(LOAD_INTENTS, forceLoad);
const updateFlowStatus = (status: FlowStatus): Promise<void> => store.dispatch(UPDATE_STATUS, status);

const setFlowName = (name: string) => store.commit(SET_FLOW_NAME, name);
const makeDirty = (changedType: ChangeType) => store.commit(MAKE_DIRTY, changedType);
const resetChangeTracker = () => store.commit(RESET_CHANGE_TRACKER);
const setIsSaveFlowInProgress = (inProgress: boolean) => store.commit(SET_IS_SAVE_FLOW_IN_PROGRESS, inProgress);

let unsubscribeMutations = ref<(() => void) | null>(null);

const flowLoaded = ref(false);
const showDeleteModal = ref(false);
const showLeaveModal = ref(false);
const showPauseFlowModal = ref(false);
const showMakeLiveModal = ref(false);
const showBotPreviewModal = ref(false);
const showUpgradeModal = ref(false);
const showNoTriggerModal = ref(false);
const leaveModal = ref<typeof ConfirmationModal | null>(null);
let navigationNext: ((value?: boolean) => void) | null = null; // Store the next callback

const isSaveButtonEnabled = computed(() => !isSaveFlowInProgress.value && hasMadeChanges.value);
const isPreviewButtonEnabled = computed(
  () => !isFlowInvalid.value && !hasMadeChanges.value && !isSaveFlowInProgress.value,
);
const canPauseFlow = computed(
  () =>
    currentFlowStatus.value === FlowStatus.Live &&
    !hasLiveParentFlows() &&
    !hasLiveParentAnswers() &&
    !hasMadeChanges.value,
);
const canDeleteFlow = computed(() => parentFlows.value.length === 0 && parentAnswers.value.length === 0);
const canMakeLive = computed(
  () =>
    !isFlowInvalid.value &&
    !hasMadeChanges.value &&
    !isSaveFlowInProgress.value &&
    !isTransitioningToAnInactiveFlow.value &&
    isBotStudioEnabled(),
);
const previewFlowErrorMessage = computed(() =>
  hasMadeChanges.value ? 'Cannot preview a flow with unsaved changes' : '',
);
const saveFlowErrorMessage = computed(() => (!hasMadeChanges.value ? 'No changes to save' : ''));
const makeLiveErrorMessage = computed(() => {
  if (isTransitioningToAnInactiveFlow.value) {
    return 'Must not transition to flow or FAQ that is not live';
  }
  if (hasMadeChanges.value) {
    return 'Cannot make flow live with unsaved changes';
  }

  return '';
});

watch(
  () => props.flowId,
  async () => {
    await loadFlows(); // if there's no flows, (refresh has happened)
    await loadIntents(); // Required to display names
    flowLoaded.value = false;
    resetTracking();
    await fetchFlow();
    flowLoaded.value = true;
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (unsubscribeMutations.value) {
    unsubscribeMutations.value();
  }
  navigationNext = null;
});

onBeforeRouteUpdate((to, from, next) => {
  // called when the route that renders this component has changed,
  // but this component is reused in the new route.
  // For example, for a route with dynamic params `/foo/:id`, when we
  // navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
  // will be reused, and this hook will be called when that happens.
  // has access to `this` component instance.

  if (hasMadeChanges.value) {
    setupLeaveModal(next);
  } else {
    next();
  }
});

onBeforeRouteLeave((to, from, next) => {
  if (hasMadeChanges.value) {
    setupLeaveModal(next);
  } else {
    next();
  }
});

async function deleteFlow() {
  appInsights.trackEvent(
    { name: 'Delete flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );

  const flowToDeleteName = flowName.value;

  try {
    await flowsApi.deleteFlow(currentWorkspace.value.id, props.flowId);
    Toast.showSuccess(`Deleted flow ${flowToDeleteName}`);
    await resetTracking();
    await router.push({ name: 'bots-flows' });
  } catch {
    Toast.showError(`Failed to delete flow ${flowToDeleteName}`);
  }
}

async function pauseFlow() {
  try {
    setIsSaveFlowInProgress(true);
    await updateFlowStatus(FlowStatus.Paused);
    appInsights.trackEvent({ name: 'Flow paused' }, { flowId: props.flowId, workspaceId: currentWorkspace.value.id });
    Toast.showSuccess(`Paused ${flowName.value}`);
  } catch (error) {
    Toast.showError(`Failed to pause flow ${flowName.value}`);
  }
  setIsSaveFlowInProgress(false);
  showPauseFlowModal.value = false;
}

async function makeFlowLive() {
  try {
    setIsSaveFlowInProgress(true);
    await updateFlowStatus(FlowStatus.Live);
    appInsights.trackEvent(
      { name: 'Made flow live' },
      { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
    );
    Toast.showSuccess(`Made flow ${flowName.value} live`);
  } catch (error) {
    Toast.showError(`Failed to make flow ${flowName.value} live`);
  }
  setIsSaveFlowInProgress(false);
  showMakeLiveModal.value = false;
}

function saveButtonClicked() {
  appInsights.trackEvent(
    { name: 'Save flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  if (!isCurrentFlowTriggerable.value) {
    showNoTriggerModal.value = true;
  } else {
    saveChanges();
  }
}

function pauseFlowClicked() {
  appInsights.trackEvent(
    { name: 'Pause flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  if (!canPauseFlow.value) {
    Toast.showError(pauseFlowErrorMessage());
    return;
  }
  showPauseFlowModal.value = true;
}

function previewButtonClicked() {
  appInsights.trackEvent({ name: 'Preview clicked' }, { flowId: props.flowId, workspaceId: currentWorkspace.value.id });
  showBotPreviewModal.value = true;
}

function makeLiveButtonClicked() {
  appInsights.trackEvent(
    { name: 'Make live clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  showMakeLiveModal.value = true;
}

async function saveChanges() {
  showNoTriggerModal.value = false;
  setIsSaveFlowInProgress(true);

  setTimeout(async () => {
    try {
      await saveFlow();
      resetTracking();
      Toast.showSuccess('Successfully saved Flow');
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      setIsSaveFlowInProgress(false);
    }
  }, 200);
}

function getDeleteConfirmationMessage() {
  return `Are you sure you want to delete <b>${HtmlFormatter.escapeHtmlCharacters(flowName.value)}</b>?`;
}

function getLeaveConfirmationMessage() {
  return `All changes you've made to <b>${HtmlFormatter.escapeHtmlCharacters(flowName.value)}</b> will be discarded. <br/>Are you sure you want to leave the page?`;
}

function getPauseConfirmationMessage() {
  return `This will prevent the flow from being triggered. Are you sure you want to continue?`;
}

function getMakeLiveConfirmationMessage() {
  return `This will make the flow active to your audience on the applicable channels. Are you sure you want to continue?`;
}

function getNoTriggerSelectedConfirmationMessage() {
  return `This flow has no triggers defined. Are you sure you want to continue?`;
}

function deleteFlowClicked() {
  if (!canDeleteFlow.value) {
    Toast.showError('Cannot delete a flow that is transitioned to by another flow or FAQ');
  } else {
    showDeleteModal.value = true;
  }
}

function isBotStudioEnabled() {
  return isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED);
}

function isPauseOptionVisible() {
  return currentFlowStatus.value === FlowStatus.Live;
}

function isMakeLiveButtonVisible() {
  return currentFlowStatus.value === FlowStatus.Paused || currentFlowStatus.value === FlowStatus.Draft;
}

function upgradeButtonClicked() {
  showUpgradeModal.value = true;
}

function hasLiveParentFlows() {
  return parentFlows.value.filter((parentFlow) => parentFlow.status === FlowStatus.Live).length > 0;
}

function hasLiveParentAnswers() {
  return parentAnswers.value.filter((parentAnswer) => parentAnswer.status === FlowStatus.Live).length > 0;
}

async function fetchFlow() {
  await loadFlow(props.flowId);
  flowLoaded.value = true;
}

function resetTracking() {
  resetChangeTracker();

  unsubscribeMutations.value = store.subscribe((mutation, state) => {
    trackChanges(mutation, state);
  });
}

function trackChanges(mutation: MutationPayload, state: any) {
  const changeType = determineChangeType(mutation.type);
  if (changeType) {
    makeDirty(changeType);
    return true;
  }
  return false;
}

function determineChangeType(type: string): ChangeType | null {
  switch (type) {
    case ADD_PATH:
    case REMOVE_PATH:
    case UPDATE_PATH:
    case ADD_STATE:
    case REMOVE_STATE:
    case UPDATE_STATE:
      return ChangeType.Paths;

    case SET_FLOW_NAME:
      return ChangeType.Name;

    case SET_TRIGGER:
      return ChangeType.Triggers;

    case UPDATE_SCHEDULE_CONDITION:
    case UPDATE_TRIGGER_CONDITIONS:
    case UPDATE_AUDIENCE_CONDITIONS:
    case UPDATE_TARGET_CONTACT_TYPES:
      return ChangeType.Conditions;

    default:
      return null;
  }
}

function setupLeaveModal(next: (value?: boolean) => void) {
  showLeaveModal.value = true;
  navigationNext = next;

  nextTick(() => {
    if (!leaveModal.value) return;
  });
}

function handleConfirmLeave() {
  showLeaveModal.value = false;
  if (navigationNext) {
    navigationNext(true); // Proceed with navigation
    navigationNext = null; // Clear after use
  }
}

function handleCancelLeave() {
  showLeaveModal.value = false;
  if (navigationNext) {
    navigationNext(false); // Cancel navigation
    navigationNext = null; // Clear after use
  }
}

function pauseFlowErrorMessage() {
  if (hasLiveParentFlows() || hasLiveParentAnswers()) {
    return 'Cannot pause a flow that is transitioned to by a live flow or FAQ';
  }
  if (hasMadeChanges.value) {
    return 'Cannot pause a flow with unsaved changes';
  }
  return '';
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_n_badge = _resolveComponent("n-badge")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_tooltip = _resolveComponent("n-tooltip")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showBotPreviewModal.value)
      ? (_openBlock(), _createBlock(BotPreviewModal, {
          key: 0,
          onCancel: _cache[0] || (_cache[0] = ($event: any) => (showBotPreviewModal.value = false)),
          flowToPreview: __props.flowId
        }, null, 8, ["flowToPreview"]))
      : _createCommentVNode("", true),
    (!flowLoaded.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 1 }))
      : _createCommentVNode("", true),
    (flowLoaded.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("nav", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_router_link, { to: { name: 'bots-flows' } }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(BackArrow, { class: "icon-1-5x" })
                  ])
                ]),
                _: 1
              }),
              _createElementVNode("h4", _hoisted_6, [
                _createVNode(EditableText, {
                  value: flowName.value,
                  onTextUpdated: setFlowName,
                  minWidth: "100px",
                  maxWidth: "600px"
                }, null, 8, ["value"]),
                _createElementVNode("div", _hoisted_7, [
                  (currentFlowStatus.value === 'live')
                    ? (_openBlock(), _createBlock(_component_n_badge, {
                        key: 0,
                        variant: "success"
                      }, {
                        default: _withCtx(() => _cache[13] || (_cache[13] = [
                          _createTextVNode("Live")
                        ])),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (currentFlowStatus.value === 'paused')
                    ? (_openBlock(), _createBlock(_component_n_badge, {
                        key: 1,
                        variant: "secondary"
                      }, {
                        default: _withCtx(() => _cache[14] || (_cache[14] = [
                          _createTextVNode("Paused")
                        ])),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (currentFlowStatus.value === 'draft')
                    ? (_openBlock(), _createBlock(_component_n_badge, {
                        key: 2,
                        variant: "default"
                      }, {
                        default: _withCtx(() => _cache[15] || (_cache[15] = [
                          _createTextVNode("Draft")
                        ])),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("p", _hoisted_9, [
                _createVNode(_component_n_tooltip, {
                  position: "bottom",
                  text: saveFlowErrorMessage.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_n_button, {
                      variant: "secondary",
                      disabled: !isSaveButtonEnabled.value,
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (saveButtonClicked())),
                      id: "save-flow-button"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(SaveIcon),
                          _cache[16] || (_cache[16] = _createTextVNode(" Save Changes"))
                        ])
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ]),
                  _: 1
                }, 8, ["text"])
              ]),
              _createElementVNode("p", _hoisted_11, [
                _createVNode(_component_n_tooltip, {
                  position: "bottom",
                  text: previewFlowErrorMessage.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_n_button, {
                      variant: "secondary--outline",
                      disabled: !isPreviewButtonEnabled.value,
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (previewButtonClicked())),
                      id: "preview-bot-button"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_12, [
                          _createVNode(PlayIcon),
                          _cache[17] || (_cache[17] = _createTextVNode(" Preview"))
                        ])
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ]),
                  _: 1
                }, 8, ["text"])
              ]),
              (!isBotStudioEnabled() && isMakeLiveButtonVisible())
                ? (_openBlock(), _createBlock(AdvancedTooltip, {
                    key: 0,
                    class: "level-item"
                  }, {
                    element: _withCtx(() => [
                      _createElementVNode("p", null, [
                        _createVNode(_component_n_button, {
                          variant: "success",
                          disabled: !canMakeLive.value,
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (makeLiveButtonClicked())),
                          id: "make-live-button"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_13, [
                              _createVNode(PlayIcon),
                              _cache[18] || (_cache[18] = _createTextVNode(" Make Live"))
                            ])
                          ]),
                          _: 1
                        }, 8, ["disabled"])
                      ])
                    ]),
                    content: _withCtx(() => _cache[19] || (_cache[19] = [
                      _createElementVNode("div", null, [
                        _createElementVNode("strong", null, "Upgrade to unlock this feature")
                      ], -1),
                      _createElementVNode("div", null, "Get flows, FAQs and more", -1)
                    ])),
                    footer: _withCtx(() => [
                      _createElementVNode("button", {
                        class: "button is-primary is-outlined is-fullwidth",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (upgradeButtonClicked()))
                      }, " Upgrade ")
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (isMakeLiveButtonVisible() && isBotStudioEnabled())
                ? (_openBlock(), _createElementBlock("p", _hoisted_14, [
                    _createVNode(_component_n_tooltip, {
                      position: "bottom",
                      text: makeLiveErrorMessage.value
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_n_button, {
                          variant: "success",
                          disabled: !canMakeLive.value,
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (makeLiveButtonClicked())),
                          id: "make-live-button"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_15, [
                              _createVNode(PlayIcon),
                              _cache[20] || (_cache[20] = _createTextVNode(" Make Live"))
                            ])
                          ]),
                          _: 1
                        }, 8, ["disabled"])
                      ]),
                      _: 1
                    }, 8, ["text"])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_16, [
                _createVNode(DropDownMenu, {
                  goLeft: true,
                  useColourOnActive: true
                }, {
                  default: _withCtx(() => [
                    (isPauseOptionVisible())
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 0,
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (pauseFlowClicked())),
                          class: "dropdown-item",
                          id: "pause-flow-option"
                        }, [
                          _createVNode(PauseIcon),
                          _cache[21] || (_cache[21] = _createTextVNode(" Pause Flow "))
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("a", {
                      onClick: deleteFlowClicked,
                      class: "dropdown-item",
                      id: "delete-flow-option"
                    }, [
                      _createVNode(DeleteIcon),
                      _cache[22] || (_cache[22] = _createTextVNode(" Delete "))
                    ])
                  ]),
                  _: 1
                })
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (flowLoaded.value)
      ? (_openBlock(), _createElementBlock("section", {
          key: 3,
          class: _normalizeClass(["section content-container", { 'is-disabled': isSaveFlowInProgress.value }])
        }, [
          _createVNode(_component_router_view)
        ], 2))
      : _createCommentVNode("", true),
    (showDeleteModal.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 4,
          id: "delete-flow-modal",
          confirmationTitle: "Delete flow",
          confirmationMessage: getDeleteConfirmationMessage(),
          onConfirm: deleteFlow,
          onCancel: _cache[7] || (_cache[7] = ($event: any) => (showDeleteModal.value = false))
        }, null, 8, ["confirmationMessage"]))
      : _createCommentVNode("", true),
    (showLeaveModal.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 5,
          id: "leave-modal",
          ref_key: "leaveModal",
          ref: leaveModal,
          confirmationTitle: "Discard Changes",
          confirmationMessage: getLeaveConfirmationMessage(),
          onConfirm: handleConfirmLeave,
          onCancel: handleCancelLeave
        }, null, 8, ["confirmationMessage"]))
      : _createCommentVNode("", true),
    (showPauseFlowModal.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 6,
          id: "pause-flow-modal",
          confirmationTitle: "Pause Flow",
          confirmationMessage: getPauseConfirmationMessage(),
          onConfirm: pauseFlow,
          onCancel: _cache[8] || (_cache[8] = ($event: any) => (showPauseFlowModal.value = false))
        }, null, 8, ["confirmationMessage"]))
      : _createCommentVNode("", true),
    (showMakeLiveModal.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 7,
          id: "make-live-modal",
          confirmationTitle: "Make Live",
          confirmationMessage: getMakeLiveConfirmationMessage(),
          onConfirm: makeFlowLive,
          onCancel: _cache[9] || (_cache[9] = ($event: any) => (showMakeLiveModal.value = false))
        }, null, 8, ["confirmationMessage"]))
      : _createCommentVNode("", true),
    (showNoTriggerModal.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 8,
          id: "no-trigger-modal",
          ref: "noTriggerModal",
          confirmationTitle: "No triggers defined",
          confirmationMessage: getNoTriggerSelectedConfirmationMessage(),
          "confirm-button-label": "Save",
          onCancel: _cache[10] || (_cache[10] = ($event: any) => (showNoTriggerModal.value = false)),
          onConfirm: _cache[11] || (_cache[11] = ($event: any) => (saveChanges()))
        }, null, 8, ["confirmationMessage"]))
      : _createCommentVNode("", true),
    (showUpgradeModal.value)
      ? (_openBlock(), _createBlock(UpgradeModal, {
          key: 9,
          onClose: _cache[12] || (_cache[12] = ($event: any) => (showUpgradeModal.value = false))
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})